// 🚀 Environment Configuration
// This file reads environment variables at build time

interface EnvironmentConfig {
  // API Configuration
  API_BASE_URL: string;
  CF_WORKER_URL: string;
  
  // PayPal Configuration
  PAYPAL_CLIENT_ID: string;
  PAYPAL_MERCHANT_ID: string;
  PAYPAL_CURRENCY: string;
  PAYPAL_INTENT: string;
  PAYPAL_COMPONENTS: string;
  
  // Stripe Configuration
  STRIPE_PUBLISHABLE_KEY: string;
  STRIPE_ENVIRONMENT: string;
  
  // reCAPTCHA Configuration
  RECAPTCHA_SITE_KEY: string;
  
  // App Configuration
  APP_NAME: string;
  APP_VERSION: string;
  ENVIRONMENT: string;
}

// 🚀 Load environment variables with validation
const loadEnvironmentConfig = (): EnvironmentConfig => {
  const config: EnvironmentConfig = {
    // API Configuration
    API_BASE_URL: import.meta.env.VITE_API_BASE_URL || '',
    CF_WORKER_URL: import.meta.env.VITE_CF_WORKER_URL || '',
    
    // PayPal Configuration
    PAYPAL_CLIENT_ID: import.meta.env.VITE_PAYPAL_CLIENT_ID || '',
    PAYPAL_MERCHANT_ID: import.meta.env.VITE_PAYPAL_MERCHANT_ID || '',
    PAYPAL_CURRENCY: import.meta.env.VITE_PAYPAL_CURRENCY || 'USD',
    PAYPAL_INTENT: import.meta.env.VITE_PAYPAL_INTENT || 'capture',
    PAYPAL_COMPONENTS: import.meta.env.VITE_PAYPAL_COMPONENTS || 'buttons',
    
    // Stripe Configuration
    STRIPE_PUBLISHABLE_KEY: import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '',
    STRIPE_ENVIRONMENT: import.meta.env.VITE_STRIPE_ENVIRONMENT || 'test',
    
    // reCAPTCHA Configuration
    RECAPTCHA_SITE_KEY: import.meta.env.VITE_RECAPTCHA_SITE_KEY || '',
    
    // App Configuration
    APP_NAME: import.meta.env.VITE_APP_NAME || 'SubGen',
    APP_VERSION: import.meta.env.VITE_APP_VERSION || '2.0.0',
    ENVIRONMENT: import.meta.env.VITE_ENVIRONMENT || 'development',
  };

  // 🚀 Validate required environment variables
  const requiredVars = [
    'VITE_API_BASE_URL',
    'VITE_CF_WORKER_URL',
    'VITE_PAYPAL_CLIENT_ID',
    'VITE_PAYPAL_MERCHANT_ID',
    'VITE_STRIPE_PUBLISHABLE_KEY',
    'VITE_RECAPTCHA_SITE_KEY',
  ];

  const missing = requiredVars.filter(key => !import.meta.env[key]);
  
  if (missing.length > 0) {
    const error = `Missing required environment variables: ${missing.join(', ')}`;
    console.error('❌ Environment configuration error:', error);
    throw new Error(error);
  }

  console.log('✅ Environment configuration loaded successfully');
  return config;
};

// 🚀 Export the validated configuration
export const env = loadEnvironmentConfig();

// 🚀 Helper function to build API URLs
export const buildApiUrl = (endpoint: string): string => {
  const baseUrl = env.API_BASE_URL || env.CF_WORKER_URL;
  if (!baseUrl) {
    throw new Error('No API base URL configured');
  }
  
  // Remove trailing slash from base URL and leading slash from endpoint
  const cleanBaseUrl = baseUrl.replace(/\/$/, '');
  const cleanEndpoint = endpoint.replace(/^\//, '');
  
  return `${cleanBaseUrl}/${cleanEndpoint}`;
};

// 🚀 Helper function to get Cloudflare Worker URL
export const getCFWorkerUrl = (): string => {
  if (!env.CF_WORKER_URL) {
    throw new Error('CF Worker URL not configured');
  }
  return env.CF_WORKER_URL;
};

// 🚀 Helper function to get PayPal SDK URL
export const getPayPalSDKUrl = (): string => {
  if (!env.PAYPAL_CLIENT_ID) {
    throw new Error('PayPal client ID not configured');
  }

  const url = new URL('https://www.paypal.com/sdk/js');
  url.searchParams.set('client-id', env.PAYPAL_CLIENT_ID);
  url.searchParams.set('currency', env.PAYPAL_CURRENCY);
  url.searchParams.set('intent', env.PAYPAL_INTENT);
  url.searchParams.set('components', env.PAYPAL_COMPONENTS);
  
  if (env.PAYPAL_MERCHANT_ID) {
    url.searchParams.set('merchant-id', env.PAYPAL_MERCHANT_ID);
  }

  return url.toString();
};

// 🚀 Helper function to get Stripe publishable key
export const getStripePublishableKey = (): string => {
  if (!env.STRIPE_PUBLISHABLE_KEY) {
    throw new Error('Stripe publishable key not configured');
  }
  return env.STRIPE_PUBLISHABLE_KEY;
};
